import React, { useEffect, useState } from "react";
import { FaCommentDots } from "react-icons/fa";
import { FiPaperclip, FiMic } from "react-icons/fi";
import { createMinute, getMinuteByPanelId, getMinuteDocuments } from "app/apis/panels";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faEye } from "@fortawesome/free-solid-svg-icons";
import { CIVIL_CASE_DIRECTOR_ROLE, CIVIL_CASE_TEAMLEADER_ROLE } from "app/utils/const";
import { useAppContext } from "app/contexts/AppContext";
import EmployeeProfile from "app/views/common_component/EmployeeProfile";
import { useTranslation } from "react-i18next";

const MinuteTab = ({ isDirectorOrHead }) => {
    const { t } = useTranslation();
    const { selectedPanel } = useAppContext()
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [files, setFiles] = useState([]);
    const [change, setChange] = useState(false);
    const employee_id = sessionStorage.getItem('employee_id');
    const [documentURL, setDocumentURL] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [minutes, setMinutes] = useState([]);
    const current_user_role = sessionStorage.getItem('roles')
    const [payLoad, setPayLoad] = useState({
        panel: selectedPanel?.uu_id,
        created_by: employee_id,
        descriptions: '',
        files: [],
    });

    useEffect(() => {
        if (!selectedPanel?.uu_id) return;
        const fetchMinutes = async () => {
            try {
                const response = await getMinuteByPanelId(selectedPanel?.uu_id);
                const { minute, minute_documents } = response?.data || {};

                // Combine minute and minute_documents
                const combinedMinutes = minute.map(min => ({
                    ...min,
                    documents: minute_documents.filter(doc => doc.minute.uu_id === min.uu_id),
                }));

                setMinutes(combinedMinutes);
            } catch (error) {
                console.error("Error has occurred:", error);
                setError(error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchMinutes();
    }, [selectedPanel?.uu_id, change]);

    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files);
        setFiles((prevFiles) => [...prevFiles, ...selectedFiles]);
        setPayLoad((prev) => ({
            ...prev,
            files: [...prev.files, ...selectedFiles],
        }));
    };

    const removeFile = (index) => {
        const newFiles = files.filter((_, i) => i !== index);
        setFiles(newFiles);
        setPayLoad((prev) => ({
            ...prev,
            files: newFiles,
        }));
    };

    const handleSend = async () => {
        if (!payLoad.descriptions && files.length === 0) return;

        const formData = new FormData();
        formData.append("panel", selectedPanel?.uu_id);
        formData.append("created_by", payLoad.created_by);
        formData.append("descriptions", payLoad.descriptions);

        if (files.length > 0) {
            files.forEach((file) => {
                formData.append("files", file);
            });
        }

        try {
            const response = await createMinute(formData);
            if (response.data) {
                setMinutes((prev) => [...prev, { ...response.data, documents: [] }]);
                setChange(!change);
                setPayLoad({
                    panel: selectedPanel?.uu_id,
                    created_by: employee_id,
                    descriptions: '',
                    files: [],
                });
                setFiles([]);
            }
        } catch (error) {
            console.error("Error sending minute:", error);
            setError(t("Failed to send minute. Please try again."));
        }
    };

    const handleView = async (id) => {
        setIsLoading(true);
        try {
            const response = await getMinuteDocuments(id, { responseType: "blob" });
            if (response.status >= 200 && response.status < 300) {
                const blob = new Blob([response.data], { type: response.headers["content-type"] || "application/pdf" });
                const url = window.URL.createObjectURL(blob);
                setDocumentURL(url);
                setIsModalOpen(true);
            } else {
                console.error("Server error:", response.status, response.statusText);
                setError(t("Failed to fetch document. Please try again."));
            }
        } catch (error) {
            console.error("Network error:", error);
            setError(t("Failed to fetch document. Please try again."));
        } finally {
            setIsLoading(false);
        }
    };

    const handleDownload = async (id) => {
        setIsDownloading(true);
        try {
            const response = await getMinuteDocuments(id, { responseType: "blob" });

            if (response.status >= 200 && response.status < 300) {
                const blob = new Blob([response.data], { type: "application/octet-stream" });
                const contentDisposition = response.headers["content-disposition"];
                const fileName = contentDisposition
                    ? contentDisposition.split("filename=")[1]?.split(";")[0]?.replace(/"/g, "")
                    : "document.pdf";

                const url = window.URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", fileName);
                document.body.appendChild(link);
                link.click();

                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            } else {
                console.error("Server error:", response.status, response.statusText);
                setError(t("Failed to download document. Please try again."));
            }
        } catch (error) {
            console.error("Network error:", error);
            setError(t("Failed to download document. Please try again."));
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <div className="relative flex flex-col h-[70vh]">
            {selectedPanel && (
                <>
                    <div className="flex-1 p-4 bg-gray-50 overflow-y-auto">
                        {minutes.map((minute, index) => (
                            <div key={index} className="flex items-start gap-2 mb-4">
                                <div className="w-full bg-white">
                                    <EmployeeProfile request={minute?.created_by} />
                                    <p className="bg-white p-2 rounded shadow">{minute?.descriptions}</p>
                                    {minute.documents.map((doc, i) => (
                                        <div key={i} className="card__file">
                                            <div className="card__file-info">
                                                <div className="card__file-name">
                                                    <span>{doc.document_type}</span>
                                                </div>
                                                <div className="flex gap-2">
                                                    <button
                                                        className="text-gray-500 hover:text-green-500"
                                                        onClick={() => handleView(doc.uu_id)}
                                                        title={t("View")}
                                                    >
                                                        <FontAwesomeIcon icon={faEye} />
                                                    </button>
                                                    <button
                                                        className="text-gray-500 hover:text-green-500"
                                                        onClick={() => handleDownload(doc.uu_id)}
                                                        title={t("Download")}
                                                    >
                                                        <FontAwesomeIcon icon={faDownload} />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                        {files.length > 0 && (
                            <div className="p-4 bg-white border-t">
                                {files.map((file, index) => (
                                    <div key={index} className="flex items-center justify-between bg-gray-200 p-2 mb-2 rounded">
                                        <span className="text-gray-700">{file.name}</span>
                                        <button
                                            type="button"
                                            onClick={() => removeFile(index)}
                                            className="text-red-500"
                                            title={t("Remove file")}
                                        >
                                            x
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                        {minutes.length === 0 && (
                            <div className="flex items-center justify-center p-4">
                                <div className="bg-white shadow-md rounded-lg p-6 text-center">
                                    <h2 className="text-xl font-semibold mb-2">{t("No Minute Available")}</h2>
                                    <p className="text-gray-600">{t("There are no Minute available for this panel.")}</p>
                                </div>
                            </div>
                        )}
                    </div>

                    {isDirectorOrHead && !selectedPanel?.closed && (
                        <div className="p-4 bg-white flex items-center border-t">
                            <label className="p-2 cursor-pointer">
                                <FiPaperclip />
                                <input
                                    type="file"
                                    accept=".pdf, image/*"
                                    className="hidden"
                                    onChange={handleFileChange}
                                    multiple
                                />
                            </label>
                            <textarea
                                type="text"
                                placeholder={t("Type your descriptions here...")}
                                className="flex-1 p-2 border rounded"
                                value={payLoad.descriptions}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        handleSend();
                                    }
                                }}
                                onChange={(e) => setPayLoad((prev) => ({ ...prev, descriptions: e.target.value }))}
                                rows={2}
                            />
                            <button className="p-2 bg-black text-white rounded" onClick={handleSend}>{t("Send")}</button>
                        </div>
                    )}

                    {isModalOpen && (
                        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50" style={{ zIndex: 1000 }}>
                            <div className="bg-white p-6 rounded-lg shadow-lg w-3/4 h-3/4 relative">
                                <button
                                    onClick={() => setIsModalOpen(false)}
                                    className="absolute top-2 right-2 text-gray-600 hover:text-red-500"
                                    title={t("Close")}
                                >
                                    ✖
                                </button>
                                <h2 className="text-[22] font-semibold mb-2">{t("Document Preview")}</h2>
                                {isLoading ? (
                                    <p>{t("Loading...")}</p>
                                ) : documentURL ? (
                                    <iframe src={documentURL} className="w-full h-[95%] border rounded-lg" title={t("Document Preview")}></iframe>
                                ) : (
                                    <p>{t("Failed to load document")}</p>
                                )}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default MinuteTab;